// src/utils/commentUtils.ts - Enhanced version
import { commentAPI } from '@/api/commentAPI';
import { CommentCreateData, CommentUpdateData } from '@/types/comments';
import { ensureHtml, toPlainText } from '@/utils/richText';

/**
 * Utility function to delete a comment and handle common logic
 */
export const deleteComment = async (commentId: string): Promise<void> => {
  try {
    await commentAPI.deleteComment(commentId);
  } catch (error) {
    console.error('Failed to delete comment:', error);
    throw error;
  }
};

/**
 * Utility function to create a comment
 */
export const createComment = async (
  taskId: string,
  html: string,
  parentId?: string,
  plainText?: string
): Promise<void> => {
  try {
    const contentHtml = html;
    const contentText = plainText ?? toPlainText(ensureHtml(html || ''));
    const commentData: CommentCreateData = {
      content: contentText,
      content_html: contentHtml,
      content_text: contentText,
      task_id: taskId,
      parent_comment_id: parentId
    };
    await commentAPI.createComment(commentData);
  } catch (error) {
    console.error('Failed to create comment:', error);
    throw error;
  }
};

/**
 * Utility function to get comments for a task
 */
export const getCommentsForTask = async (taskId: string): Promise<any[]> => {
  try {
    return await commentAPI.getComments(taskId);
  } catch (error) {
    console.error('Failed to fetch comments:', error);
    return [];
  }
};

/**
 * Utility function to format comments for activity display
 */
export const formatCommentsForActivity = (comments: any[]): any[] => {
  return comments.map(comment => ({
    id: comment.id,
    user: comment.user_name,
    date: new Date(comment.created_at).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    }),
    content: comment.content_html || comment.content,
    avatar: getInitials(comment.user_name),
    type: 'comment'
  }));
};

/**
 * Helper function to get user initials
 */
const getInitials = (name: string): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const updateComment = async (commentId: string, html: string, plainText?: string): Promise<void> => {
  try {
    const contentHtml = html;
    const contentText = plainText ?? toPlainText(ensureHtml(html || ''));
    const updateData: CommentUpdateData = {
      content: contentText,
      content_html: contentHtml,
      content_text: contentText
    };
    await commentAPI.updateComment(commentId, updateData);
  } catch (error) {
    console.error('Failed to update comment:', error);
    throw error;
  }
};